import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { useAppSelector } from "../../../redux/hooks";
import { formatMetricValue, type FormatOptions } from "../../../utils/formatNumber";
import type { CompareHighlight } from "../../../modules/merchandising/utils/metrics";
import { HoverInfo } from "../../core/basic/Tooltip";
import { MetricChangeRow } from "../../core/blocks/MetricChangeRow";

interface Props {
  value: number | null;
  formatOptions?: FormatOptions;
  compareHighlight?: CompareHighlight;
  deltaValue?: number | null;
  percentValue?: number | null;
  previousValue?: number | null;
  /** Coverage info for goal-style metrics. When `withGoal < total`, renders an "N / M days with goals" hint below the value. */
  goalCoverage?: { withGoal: number; total: number } | null;
}

function MetricTooltip({
  values,
  valueFormat,
  deltaFormat,
  percentFormat,
}: {
  values: {
    current: number | null;
    previous?: number | null;
    absolute?: number | null;
    percent?: number | null;
  };
  valueFormat: FormatOptions;
  deltaFormat: FormatOptions;
  percentFormat: FormatOptions;
}) {
  const { t } = useTranslation(["common"]);
  const rows: [string, string][] = [
    [t(($) => $.common.metricTooltip.Current), formatMetricValue(values.current, valueFormat)],
    [t(($) => $.common.metricTooltip.Compared), formatMetricValue(values.previous, valueFormat)],
    [t(($) => $.common.metricTooltip.Absolute), formatMetricValue(values.absolute, deltaFormat)],
    [t(($) => $.common.metricTooltip.Percent), formatMetricValue(values.percent, percentFormat)],
  ];
  return (
    <VStack gap={"xsmall"} alignItems={"start"} minWidth={"160px"}>
      {rows.map(([label, value]) => (
        <HStack key={label} gap={"medium"} justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
          <span
            className={css({
              color: "grey",
              fontSize: "xsmall",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            })}
          >
            {label}
          </span>
          <span
            className={css({
              color: "white",
              fontSize: "small",
              fontWeight: "600",
            })}
          >
            {value !== "-" ? value : "-"}
          </span>
        </HStack>
      ))}
    </VStack>
  );
}

export function MetricCell({
  value,
  formatOptions = {
    format: "number",
    compact: true,
    decimalPlaces: 1,
  },
  compareHighlight = "positiveIsGood",
  deltaValue,
  percentValue,
  previousValue,
  goalCoverage,
}: Props) {
  const { t } = useTranslation(["common"]);
  deltaValue = formatOptions.format === "percent" && deltaValue ? deltaValue * 100 : deltaValue;
  let marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  marketPlace = formatOptions.marketPlace ?? marketPlace;

  const baseFormat = { ...formatOptions, marketPlace };
  const deltaFormat: FormatOptions = {
    ...baseFormat,
    addPlus: true,
    format: baseFormat.format === "percent" ? "number" : baseFormat.format,
  };
  const valueFullFormat: FormatOptions = { ...baseFormat, compact: false };

  const displayValue = formatMetricValue(value, baseFormat);

  const hasTooltipContent = previousValue != null || deltaValue != null || percentValue != null;

  return (
    <VStack gap={"xsmall"} alignItems={"end"} width={"100%"} paddingY="xsmall" paddingX={"small"} lineHeight={"1"}>
      <HStack gap={"xsmall"} justifyContent={"end"} alignItems={"center"}>
        <span>{displayValue}</span>
        {hasTooltipContent && (
          <HoverInfo iconCss={{ color: "grey", fontSize: "xsmall" }}>
            <MetricTooltip
              values={{
                current: value,
                previous: previousValue,
                absolute: deltaValue,
                percent: percentValue,
              }}
              valueFormat={valueFullFormat}
              deltaFormat={deltaFormat}
              percentFormat={{
                format: "percent",
                compact: false,
                decimalPlaces: 1,
                addPlus: true,
                marketPlace,
              }}
            />
          </HoverInfo>
        )}
      </HStack>
      <MetricChangeRow
        delta={deltaValue}
        percent={percentValue}
        highlight={compareHighlight}
        deltaFormat={{ ...deltaFormat, compact: true }}
        marketPlace={marketPlace}
      />
      {goalCoverage &&
        goalCoverage.withGoal > 0 &&
        goalCoverage.total > 0 &&
        goalCoverage.withGoal < goalCoverage.total && (
          <span className={css({ fontSize: "xsmall", color: "grey.500", lineHeight: 1 })}>
            {t(($) => $.common["{withGoal} / {total} days with goals"], {
              withGoal: goalCoverage.withGoal,
              total: goalCoverage.total,
            })}
          </span>
        )}
    </VStack>
  );
}
